import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import useMediaQueries from "../../services/media";
import "./styles.scss";

interface CollageGalleryProps {
  images: Array<string>;
}

function CollageGallery({ images }: CollageGalleryProps) {
  gsap.registerPlugin(ScrollTrigger);
  const [isMobileView] = useMediaQueries();
  const galleryRef = useRef<HTMLDivElement>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const scrollPositionRef = useRef<number>(0);

  // Asymmetric tile layout patterns - defines size classes for interesting compositions
  const getTileClass = (index: number): string => {
    const patterns = [
      'tile-large',      // 0 - wide horizontal
      'tile-medium',     // 1 - square-ish
      'tile-tall',       // 2 - vertical
      'tile-wide',       // 3 - extra wide
      'tile-small',      // 4 - compact
      'tile-medium-tall', // 5 - medium vertical
      'tile-large',      // 6 - wide horizontal
      'tile-small',      // 7 - compact
      'tile-wide',       // 8 - extra wide
      'tile-medium',     // 9 - square-ish
      'tile-tall',       // 10 - vertical
      'tile-medium-tall', // 11 - medium vertical
    ];
    return patterns[index % patterns.length];
  };

  const openLightbox = (image: string) => {
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
    setLightboxImage(image);
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollPositionRef.current}px`;
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setLightboxImage(null);
    
    const scrollPos = scrollPositionRef.current;
    
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    
    // Restore scroll position instantly without smooth scrolling
    if (isMobileView) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, left: 0, behavior: 'instant' as ScrollBehavior });
      });
    } else {
      window.scrollTo({ top: scrollPos, left: 0, behavior: 'instant' as ScrollBehavior });
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isLightboxOpen) {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isLightboxOpen]);

  useEffect(() => {
    if (!isMobileView && galleryRef.current) {
      const tiles = galleryRef.current.querySelectorAll('.gallery-tile');
      
      tiles.forEach((tile, index) => {
        gsap.fromTo(
          tile,
          {
            opacity: 0,
            y: 50,
            scale: 0.95,
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            ease: "power2.out",
            scrollTrigger: {
              trigger: tile,
              start: "top 85%",
              end: "top 50%",
              toggleActions: "play none none reverse",
            },
          }
        );

        // Subtle parallax effect on hover
        tile.addEventListener('mouseenter', () => {
          gsap.to(tile, {
            scale: 1.02,
            zIndex: 10,
            duration: 0.3,
            ease: "power2.out",
          });
        });

        tile.addEventListener('mouseleave', () => {
          gsap.to(tile, {
            scale: 1,
            zIndex: 1,
            duration: 0.3,
            ease: "power2.out",
          });
        });
      });
    }
  }, [isMobileView, images]);

  return (
    <>
      <div className="collage-gallery" ref={galleryRef}>
        <div className="gallery-grid">
          {images.map((image, index) => (
            <div
              key={index}
              className={`gallery-tile ${getTileClass(index)}`}
              onClick={() => openLightbox(image)}
            >
              <div className="tile-inner">
                <img src={image} alt="" loading="lazy" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {isLightboxOpen && lightboxImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              ✕
            </button>
            <img src={lightboxImage} alt="" />
          </div>
        </div>
      )}
    </>
  );
}

export default CollageGallery;
